import React, { useState, useEffect, useRef } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { Button, LinearProgress, Paper } from "@mui/material";
import { CheckCircleOutline, Refresh } from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { isMobile } from "react-device-detect";
import logoImage from "../../../assets/images/logo.png";
import callApi from "../../../services/callApi";
import Loader from "../../../components/Loader/Loader";

export default function ValidadorTransferencia() {
  const location = useLocation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [titulo, setTitulo] = useState("Nombre Profesional");
  const [profesion, setProfesion] = useState("Profesion");
  const [servicio_nombre, setServicioNombre] = useState("Nombre Servicio");
  const [servicio_precios, setServicioPrecios] = useState("Valor servicio");
  const [servicio_modalidad, setServicioModalidad] = useState("Modalidad");
  const [fecha_formateada, setFechaFormateada] = useState("");
  const [hourss, setHourss] = useState("");

  // Estados del progreso de validación
  const [progress, setProgress] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(0);
  const [currentSubprocess, setCurrentSubprocess] = useState(0);
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [verificationAttempts, setVerificationAttempts] = useState(0);
  const timerRef = useRef(null);
  const apiCallMadeRef = useRef(false);
  const redirectTimerRef = useRef(null);

  const TOTAL_DURATION = 55000; // 55 segundos
  const API_CALL_TIME = 50000; // Llamar a la API en el segundo 50
  const UPDATE_INTERVAL = 100; // 100ms
  const SUCCESS_REDIRECT_DELAY = 3000; // 3 segundos antes de redirigir
  const MAX_VERIFICATION_ATTEMPTS = 2; // Número máximo de intentos antes de redirigir a WhatsApp

  const phases = [
    {
      titulo: "Ingresando a la cuenta bancaria",
      subprocesos: [
        "Verificando credenciales de acceso",
        "Estableciendo conexión segura",
        "Accediendo al portal bancario",
      ],
    },
    {
      titulo: "Revisando transacciones recibidas",
      subprocesos: [
        "Cargando historial de movimientos",
        "Filtrando transferencias recientes",
        "Analizando depósitos del día",
      ],
    },
    {
      titulo: "Buscando la transferencia del paciente",
      subprocesos: [
        "Verificando datos del remitente",
        "Confirmando monto de la transacción",
        "Validando hora de la transferencia",
      ],
    },
  ];

  useEffect(() => {
    // Scroll instantáneo a la parte superior
    window.scrollTo(0, 0);
  }, []);


  useEffect(() => {
    if (location.state && location.state.transferenciaInfo) {
      const transf = location.state.transferenciaInfo;

      if (transf.profesional_nombres && transf.profesional_apellidos) {
        setTitulo(
          `${transf.profesional_nombres} ${transf.profesional_apellidos}`
        );
      }
      if (transf.servicio_precio) {
        const precio = new Intl.NumberFormat("es-CL", {
          style: "currency",
          currency: "CLP",
          minimumFractionDigits: 0,
        }).format(transf.servicio_precio);
        setServicioPrecios(precio);
      }
      if (transf.profesional_profesion)
        setProfesion(transf.profesional_profesion);
      if (transf.servicio_nombre) setServicioNombre(transf.servicio_nombre);
      if (transf.servicio_modalidad)
        setServicioModalidad(transf.servicio_modalidad);
      if (transf.fecha_agenda)
        setFechaFormateada(transf.fecha_agenda_corta || transf.fecha_agenda);
      if (transf.hora_agenda) setHourss(transf.hora_agenda);
    }
  }, [location.state]);

  const calculateProgress = (elapsed) => {
    const normalizedTime = Math.min(elapsed / TOTAL_DURATION, 1);
    return Math.min(
      100,
      Math.round((1 - Math.pow(1 - normalizedTime, 2)) * 100)
    );
  };

  const updatePhaseAndSubprocess = (elapsed) => {
    const totalPhases = phases.length;
    const totalSubprocessesPerPhase = phases[0].subprocesos.length;

    const phaseTimeSlice = TOTAL_DURATION / totalPhases;
    const subprocessTimeSlice = phaseTimeSlice / totalSubprocessesPerPhase;

    const currentPhaseIndex = Math.min(
      Math.floor(elapsed / phaseTimeSlice),
      totalPhases - 1
    );
    const phaseElapsed = elapsed % phaseTimeSlice;
    const currentSubprocessIndex = Math.min(
      Math.floor(phaseElapsed / subprocessTimeSlice),
      totalSubprocessesPerPhase - 1
    );

    setCurrentPhase(currentPhaseIndex);
    setCurrentSubprocess(currentSubprocessIndex);
    setMessage(phases[currentPhaseIndex].subprocesos[currentSubprocessIndex]);
  };

  const callValidationApi = async () => {
    try {
      const url = `/transferencia/transferenciaValidarEstado?transferencia_id=${location.state.transferencia_id}`;
      const response = await callApi(url, "get", null);

      if (response) {
        if (response.aprobada === true) {
          setStatus("success");

          if (redirectTimerRef.current) {
            clearTimeout(redirectTimerRef.current);
          }

          redirectTimerRef.current = setTimeout(() => {
            navigate("/pro/perfil/exito-transferencia", {
              state: {
                transferencia_id: location.state.transferencia_id,
                ...location.state,
              },
            });
          }, SUCCESS_REDIRECT_DELAY);
        } else {
          setStatus("error");
          setErrorMessage(
            response.mensaje || "Transferencia no encontrada o no válida"
          );
        }
      } else {
        setStatus("error");
        setErrorMessage("No se pudo validar la transferencia");
      }
    } catch (error) {
      console.error("Error al validar estado de transferencia:", error);
      setStatus("error");
      setErrorMessage("Error al verificar la transferencia");
    }
  };

  const startProgress = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (redirectTimerRef.current) {
      clearTimeout(redirectTimerRef.current);
      redirectTimerRef.current = null;
    }

    setProgress(0);
    setCurrentPhase(0);
    setCurrentSubprocess(0);
    setStatus("loading");
    setErrorMessage("");
    apiCallMadeRef.current = false;
    setMessage(phases[0].subprocesos[0]);

    setStartTime(null);

    setTimeout(() => {
      setStartTime(Date.now());
    }, 50);
  };

  useEffect(() => {
    startProgress();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!startTime) return;

    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTime;

      if (elapsed >= API_CALL_TIME && !apiCallMadeRef.current) {
        apiCallMadeRef.current = true;
        callValidationApi();
      }

      if (elapsed >= TOTAL_DURATION) {
        setProgress(100);
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        if (status === "loading") {
          setStatus("error");
          setErrorMessage("Se agotó el tiempo de espera para la verificación");
        }
      } else {
        setProgress(calculateProgress(elapsed));
        updatePhaseAndSubprocess(elapsed);
      }
    }, UPDATE_INTERVAL);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [startTime]);

  const handleRetry = async () => {
    try {
      const newAttemptCount = verificationAttempts + 1;
      setVerificationAttempts(newAttemptCount);

      setStatus("loading");

      if (location.state && location.state.transferencia_id) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }

        if (redirectTimerRef.current) {
          clearTimeout(redirectTimerRef.current);
          redirectTimerRef.current = null;
        }

        await callApi(`/transferencia/transferenciaMovimientos`, "post", {
          transferencia_id: location.state.transferencia_id,
        });

        startProgress();
      } else {
        console.error("No hay transferencia_id disponible para reintentar");
        setStatus("error");
        setErrorMessage(
          "Error: No se pudo encontrar el ID de la transferencia"
        );
      }
    } catch (error) {
      console.error("Error al registrar el movimiento para reintentar:", error);
      setStatus("error");
      setErrorMessage("Error al iniciar la verificación nuevamente");
    }
  };

  const transformModalidad = (modalidad) => {
    switch (modalidad) {
      case "consultorio":
        return "Consulta presencial";
      case "domicilio":
        return "Consulta a domicilio";
      case "online":
        return "Consulta online";
      default:
        return modalidad;
    }
  };

  const formatearFechaSinAnio = (fecha) => {
    if (!fecha) return "";
    return fecha.slice(0, -5);
  };

  const getRetryButtonText = () => {
    return "VERIFICAR NUEVAMENTE";
  };

  const SuccessMessage = () => (
    <Box
      sx={{
        backgroundColor: "#e6f7ee",
        color: "#2e7d32",
        border: "1px solid #a5d6a7",
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        overflow: "hidden",
        maxWidth: "100%",
        boxSizing: "border-box",
        p: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 0.5,
        }}
      >
        <CheckCircleOutline
          sx={{
            mr: 1,
            fontSize: isMobile ? "18px" : "20px",
          }}
        />
        <Typography
          sx={{
            fontFamily: "Quicksand-Bold",
            fontSize: { md: "16px", xs: "14px" },
          }}
        >
          Pago verificado con éxito
        </Typography>
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontSize: { md: "16px", xs: "14px" },
          fontFamily: "Quicksand-Regular",
          lineHeight: 1.3,
          ml: 4,
        }}
      >
        La transferencia bancaria ha sido encontrada y verificada correctamente.
        Serás redirigido en unos segundos...
      </Typography>
    </Box>
  );

  return (
    <>
      <Loader isLoading={isLoading} />
      <img
        src={logoImage}
        alt="Clinifica Logo"
        style={{
          width: isMobile ? "150px" : "200px",
          height: "auto",
          paddingTop: "15px",
          display: "block",
          marginLeft: "auto",
          marginRight: "auto",
          padding: 15,
        }}
      />
      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "#fbfafe",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 2,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262",
          fontSize: { xs: "14px", md: "16px" },
          mb: 0
        }}>
          Estas pagando tu sesión con:
        </Typography>
        <Typography
          sx={{
            mt: -0.5,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "18px", md: "20px" },
            color: "#191C1F",
            mb: 0
          }}
        >
          {titulo}
        </Typography>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            mt: -0.5,
            fontSize: { xs: "14px", md: "16px" },
            color: "default",
            mb: 0
          }}
        >
          {profesion}
        </Typography>
        <Typography sx={{ 
          fontFamily: "Quicksand-Regular",
          color: "#626262",
          mt: 2,
          fontSize: { xs: "14px", md: "16px" },
          mb: 0
        }}>
          Por el servicio de:
        </Typography>
        <Typography
          sx={{
            mt: -0.5,
            fontFamily: "Quicksand-Bold",
            fontSize: { xs: "18px", md: "20px" },
            color: "#191C1F",
            mb: 0
          }}
        >
          {servicio_nombre}
        </Typography>
        <Typography
          sx={{ 
            fontFamily: "Quicksand-Regular", 
            mt: -0.5,
            fontSize: { md: "16px", xs: "14px" },
            color: "#191C1F",
            mb: 0
          }}
        >
          {servicio_precios}
        </Typography>
        <Box
          sx={{
            display: "inline-block",
            backgroundColor: "#f0f4ff",
            color: "#3B82F6",
            fontFamily: "Quicksand-Medium",
            fontSize: { md: "15px", xs: "13px" },
            borderRadius: 2,
            paddingLeft: 0.75,
            paddingRight: 0.75,
            paddingTop: 0.25,
            paddingBottom: 0.25,
            marginLeft: -0.75,
            mt: -0.75,
            textAlign: "left",
          }}
        >
          {transformModalidad(servicio_modalidad)}
        </Box>
        {fecha_formateada && (
          <Typography
            sx={{
              mt: 0,
              color: "#007FFF",
              fontFamily: "Quicksand-Bold",
              fontSize: { md: "16px", xs: "14px" },
            }}
          >
            {formatearFechaSinAnio(fecha_formateada)} {hourss ? "-" : ""}{" "}
            {hourss}
          </Typography>
        )}
      </Box>

      <Typography
        sx={{
          fontFamily: "Quicksand-Bold",
          fontSize: { md: "24px", xs: "20px" },
          textAlign: "center",
          color: "#191C1F",
          mt: 3,
          mb: 1
        }}
      >
        Estamos Validando tu transferencia
      </Typography>

      <Box
        sx={{
          maxWidth: isMobile ? "94%" : "800px",
          width: isMobile ? "94%" : "90%",
          mx: "auto",
          bgcolor: "white",
          boxShadow: 1,
          marginTop: 2,
          marginBottom: 8,
          borderRadius: 2,
          p: isMobile ? 2 : 4,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", md: "66.67%" },
            mx: "auto",
          }}
        >
          {status === "loading" && (
            <>
              <Typography
                sx={{
                  mt: 3,
                  mb: 1,
                  fontFamily: "Quicksand-Regular",
                  fontSize: { md: "16px", xs: "14px" },
                  color: "#191C1F",
                  textAlign: "center",
                }}
              >
                {phases[currentPhase].titulo}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                  pt: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Regular",
                    fontSize: { md: "16px", xs: "14px" },
                    color: "#191C1F",
                  }}
                >
                  {message}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Bold",
                    fontSize: { md: "16px", xs: "14px" },
                    color: "#2669A8",
                  }}
                >
                  {progress}%
                </Typography>
              </Box>

              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 15,
                  borderRadius: 5,
                  backgroundColor: "#e7eff5",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: "#3B82F6",
                    transition: "transform 100ms linear",
                  },
                }}
              />
            </>
          )}

          {status === "success" && <SuccessMessage />}

          {status === "error" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  backgroundColor: "#FFF9E6",
                  p: 1,
                  paddingBottom: 2,
                  paddingTop: 2,
                  borderRadius: 1,
                  mt: 1,
                  mb: 2,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Quicksand-Bold",
                    fontSize: { md: "16px", xs: "14px" },
                    color: "#7D6E25",
                    textAlign: "center",
                  }}
                >
                  No encontramos tu transferencia en nuestros registros
                  bancarios, si ya transferiste, selecciona{" "}
                  {getRetryButtonText()}.
                </Typography>
              </Box>

              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={handleRetry}
                fullWidth
                sx={{
                  fontSize: { xs: "14px", md: "16px", lg: "16px" },
                  fontFamily: "Quicksand-Bold",
                  backgroundColor: "#3B82F6",
                  "&:hover": { backgroundColor: "#2563EB" },
                  borderRadius: 2,
                  paddingTop: 2,
                  paddingBottom: 2,
                  mt: 0,
                  textTransform: "none",
                }}
              >
                {getRetryButtonText()}
              </Button>

              {verificationAttempts >= MAX_VERIFICATION_ATTEMPTS && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  <Typography
                    component="a"
                    href="https://wa.me/56920809101?text=Hola,%20necesito%20ayuda%20con%20una%20transferencia.%20¿Me%20ayudan%20por%20favor?%20"
                    sx={{
                      color: "#3B82F6",
                      textDecoration: "none",
                      cursor: "pointer",
                      fontFamily: "Quicksand-Regular",
                      fontSize: { md: "16px", xs: "14px" },
                      "&:hover": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    Contactar a soporte
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

